import { useEffect, useState } from "react";
import Button from "components/general/Button";
import Text from "components/general/Text";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import { DateFilterProps } from "./types";
import ModalWrapper from "../ModalWrapper";
import styles from "./styles.module.scss";

export default function DateFilter({
  isVisible,
  setIsVisible,
  isRange,
  isPrimary = true,
}: DateFilterProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { RangePicker } = DatePicker;
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);
  // "All Time" intentionally has no end date; every other period needs both ends
  const [isAllTime, setIsAllTime] = useState(false);

  const setPresetRange = (dates: [Dayjs | null, Dayjs | null]) => {
    setIsAllTime(false);
    setRange(dates);
  };

  const LeftBtns: [string, () => void][] = [
    [
      "Today",
      () => {
        setPresetRange([dayjs(), dayjs()]);
      },
    ],
    [
      "Yesterday",
      () => {
        setPresetRange([dayjs().add(-1, "day"), dayjs().add(-1, "day")]);
      },
    ],
    [
      "Last Week",
      () => {
        setPresetRange([dayjs().add(-1, "week"), dayjs()]);
      },
    ],
    [
      "Last Month",
      () => {
        setPresetRange([dayjs().add(-1, "month"), dayjs()]);
      },
    ],
    [
      "Last Year",
      () => {
        setPresetRange([dayjs().add(-1, "year"), dayjs()]);
      },
    ],
    [
      "All Time",
      () => {
        setRange([dayjs(new Date()), null]);
        setIsAllTime(true);
      },
    ],
  ];

  const startDateStr = dayjs(range[0]).isValid()
    ? range[0]?.format("YYYY-MM-DD") || ""
    : "";
  const endDateStr = dayjs(range[1]).isValid()
    ? range[1]?.format("YYYY-MM-DD") || ""
    : "";

  const handleInitialParams = () => {
    if (isPrimary) {
      const startDate =
        searchParams.get("startDate") || dayjs().format("YYYY-MM-DD");
      const endDate = searchParams.get("endDate")
        ? dayjs(searchParams.get("endDate"))
          .add(-1, "days")
          .format("YYYY-MM-DD")
        : dayjs().add(1, "days").format("YYYY-MM-DD");
      setRange([dayjs(startDate), dayjs(endDate)]);
    } else {
      const startDate =
        searchParams.get("summaryStartDate") || dayjs().format("YYYY-MM-DD");
      const endDate = searchParams.get("summaryEndDate")
        ? dayjs(searchParams.get("summaryEndDate"))
          .add(-1, "days")
          .format("YYYY-MM-DD")
        : dayjs().add(1, "days").format("YYYY-MM-DD");
      setRange([dayjs(startDate), dayjs(endDate)]);
    }
  };

  useEffect(() => {
    // handleInitialParams();

    setIsAllTime(false);
    setRange([
      searchParams.get("startDate")
        ? dayjs(searchParams.get("startDate"))
        : null,
      searchParams.get("endDate") ? dayjs(searchParams.get("endDate")) : null,
    ]);
  }, [isVisible]);

  const onConfirm = () => {
    if (isIncompleteRange) return;
    if (isPrimary) {
      searchParams.set("page", "1");
      searchParams.set("startDate", startDateStr);
      searchParams.set("endDate", endDateStr);
    } else {
      searchParams.set("summaryStartDate", startDateStr);
      searchParams.set("summaryEndDate", endDateStr);
    }
    setSearchParams(searchParams);
    setIsVisible(false);
  };

  // Picking dates by hand always starts a new manual period
  const onChange = (dates: null | (Dayjs | null)[]) => {
    setIsAllTime(false);
    setRange((dates ?? [null, null]) as [Dayjs | null, Dayjs | null]);
  };

  const isSameDay = (a: Dayjs | null, b: Dayjs | null) =>
    a && b ? a.isSame(b, "day") : a === b;

  // The range inputs are hidden, so the picker can't move focus to the end
  // field (rc-picker v4 keeps writing every click into the start). Track the
  // two clicks ourselves: 1st click starts a period, 2nd click closes it.
  const onRangeCalendarChange = (dates: null | (Dayjs | null)[]) => {
    const [nextStart, nextEnd] = dates ?? [null, null];
    const clicked = !isSameDay(nextStart, range[0])
      ? nextStart
      : !isSameDay(nextEnd, range[1])
        ? nextEnd
        : nextStart ?? nextEnd;
    if (!clicked) return;

    setIsAllTime(false);
    const [start, end] = range;
    if (start && !end && !isAllTime) {
      setRange(clicked.isBefore(start, "day") ? [clicked, start] : [start, clicked]);
    } else {
      setRange([clicked, null]);
    }
  };

  const [rangeStart, rangeEnd] = range ?? [null, null];
  const hasStart = Boolean(rangeStart && dayjs(rangeStart).isValid());
  const hasEnd = Boolean(rangeEnd && dayjs(rangeEnd).isValid());

  // Both ends are required, except for the "All Time" preset
  const isIncompleteRange = isAllTime ? !hasStart : !hasStart || !hasEnd;

  // Inclusive count, compared by calendar day so time-of-day can't skew it
  const daysCount =
    hasStart && hasEnd
      ? dayjs(rangeEnd).startOf("day").diff(dayjs(rangeStart).startOf("day"), "day") + 1
      : 0;

  const getSelectedLabel = () => {
    if (isAllTime) return "All time";
    if (hasStart && !hasEnd) return "Select an end date";
    return `${daysCount} ${daysCount === 1 ? "day" : "days"}`;
  };
  return (
    <ModalWrapper
      headerTitle="Filter by Date"
      size="large"
      isFooterHidden
      isVisible={isVisible}
      setIsVisible={setIsVisible}
    >
      <div className={styles.container}>
        <div className={styles.bodySection}>
          <div className={styles.leftBtnsContainer}>
            <Text className={styles.leftTitle}>Predefined periods:</Text>
            <div className={styles.leftBtns}>
              {LeftBtns.map((item, index) => {
                if (index === LeftBtns.length - 1)
                  return (
                    <Button key={item[0]} onClick={() => item[1]()}>
                      {item[0]}
                    </Button>
                  );
                return (
                  <Button
                    key={item[0]}
                    color="dark"
                    isOutline
                    btnClassName={styles.customBtn}
                    onClick={() => item[1]()}
                  >
                    {item[0]}
                  </Button>
                );
              })}
            </div>
          </div>
          <div
            className={styles.datePickersContainer}
            id="datePickersContainer"
          >
            {isRange ? (
              <RangePicker
                getPopupContainer={(trigger: HTMLElement | null) => {
                  return (
                    (trigger?.parentNode as HTMLElement) ??
                    document.getElementById("datePickersContainer")
                  );
                }}
                value={range}
                // onChange={onChange}
                onCalendarChange={onRangeCalendarChange}
                open
                popupClassName={styles.popup}
                style={{
                  display: "none",
                }}
              />
            ) : (
              <DatePicker
                getPopupContainer={(trigger: HTMLElement | null) => {
                  return (
                    (trigger?.parentNode as HTMLElement) ??
                    document.getElementById("datePickersContainer")
                  );
                }}
                value={range[0]}
                onChange={(v) => onChange([v, v])}
                open
                popupClassName={styles.popup}
                style={{
                  display: "none",
                }}
              />
            )}
          </div>
        </div>
        <div className={`${styles.modalContainer} ${styles.footer}`}>
          <div className={styles.count}>
            <Text className={styles.first}>Selected: </Text>
            <Text className={styles.second}>{getSelectedLabel()}</Text>
          </div>
          <Button onClick={onConfirm} disabled={isIncompleteRange}>
            Confirm
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
}
